import React, { useState, useEffect, useRef } from 'react';
import SharedModal from '../../shared/SharedModal';
import ProjectDataForm from './components/ProjectDataForm';
import ProjectTeamForm from '../ProjectTeam/ProjectTeamForm';
import ProjectAnalysisTab from './components/ProjectAnalysisTab';
import '../Projects.css';
import './ProjectModal.css';

/**
 * @file ProjectDetailModal.jsx
 * @description Kombiniertes Modal für die Projektverwaltung (Stammdaten & Team).
 * Jetzt basierend auf der neuen SharedModal Komponente.
 */
export default function ProjectDetailModal({ isOpen, onClose, project, mode, onSubmit, onDelete, projects, deletedProjects = [], currentUser, initialTab = 'analysis' }) {
    const [activeTab, setActiveTab] = useState(mode === 'create' ? 'data' : initialTab); // 'analysis' | 'data' | 'team'
    const [initialTeamTabs, setInitialTeamTabs] = useState(null);
    const dataFormRef = useRef(null);
    const [tempTeamMembers, setTempTeamMembers] = useState([]);
    const [currentMode, setCurrentMode] = useState(mode);

    useEffect(() => {
        if (isOpen) {
            const backupStr = sessionStorage.getItem('projectModalBackup_internal');
            if (backupStr) {
                try {
                    const backup = JSON.parse(backupStr);
                    sessionStorage.removeItem('projectModalBackup_internal');
                    setActiveTab(backup.activeTab || (mode === 'create' ? 'data' : initialTab));
                    setCurrentMode(backup.currentMode || mode);
                    setTempTeamMembers(backup.tempTeamMembers || []);
                    if (backup.teamTabsState) {
                        setInitialTeamTabs(backup.teamTabsState);
                    }
                    return;
                } catch(e) {}
            }
            setActiveTab(mode === 'create' ? 'data' : initialTab);
            setCurrentMode(mode);
            setTempTeamMembers([]);
            setInitialTeamTabs(null);
        }
    }, [isOpen, project, mode, initialTab]);

    const handleNavigateAway = (teamTabsState) => {
        const formData = dataFormRef.current ? dataFormRef.current.getFormState() : project;
        sessionStorage.setItem('projectModalBackup', JSON.stringify({
            isOpen: true,
            activeTab,
            currentMode,
            tempTeamMembers,
            project: formData,
            teamTabsState
        }));
    };

    if (!isOpen || (!project && mode !== 'create')) return null;

    const isCreate = currentMode === 'create';
    const isView = currentMode === 'view';

    const handleGlobalSave = () => {
        if (dataFormRef.current) {
            const isValid = dataFormRef.current.submitForm();
            if (!isValid) {
                setActiveTab('data'); // Sprung zurück zu Stammdaten bei Fehlern
            }
        }
    };

    // --- Footer Buttons ---
    const footerActions = (
        <div style={{ display: 'flex', gap: '10px' }}>
            {isView ? (
                <>
                    <button type="button" onClick={onClose} className="btn-cancel" style={{ minWidth: '120px' }}>Schließen</button>
                    {project?.can_edit_project && project?.status !== 'deleted' && (
                        <button type="button" onClick={() => setCurrentMode('edit')} className="btn-primary" style={{ minWidth: '120px' }}>Bearbeiten</button>
                    )}
                </>
            ) : (
                <>
                    <button type="button" onClick={onClose} className="btn-cancel" style={{ minWidth: '120px' }}>Abbrechen</button>
                    {!isCreate && currentMode === 'edit' && project?.can_delete_project && (
                        <button type="button" onClick={() => onDelete(project)} className="btn-danger" style={{ minWidth: '120px' }}>Löschen</button>
                    )}
                    <button type="button" onClick={handleGlobalSave} className="btn-save" style={{ minWidth: '200px', whiteSpace: 'nowrap' }}>
                        {isCreate ? 'Erstellen' : 'Speichern'}
                    </button>
                </>
            )}
        </div>
    );

    // --- Title with Subtitle ---
    const modalTitle = (
        <div>
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.25rem' }}>{isCreate ? 'Neues Projekt' : project?.name}</h3>
            {!isCreate && <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 'normal' }}>Projekt-Nr: {project?.project_number}</span>}
        </div>
    );

    return (
        <SharedModal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            footer={footerActions}
            size={activeTab === 'team' ? 'xlarge' : 'large'}
            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                
                {/* TABS (Directly under Header) */}
                <div className="modal-tabs" style={{ 
                    display: 'flex', 
                    gap: '5px', 
                    padding: '10px 24px', 
                    background: '#f1f5f9', /* Slightly darker gray for contrast */
                    borderTop: '1px solid #e2e8f0', /* Separator to Header */
                    borderBottom: '1px solid #e2e8f0', /* Separator to Body */
                    flexShrink: 0 
                }}>
                    {!isCreate && (
                        <button
                            type="button"
                            onClick={() => setActiveTab('analysis')}
                            className={`co-segmented-control-btn ${activeTab === 'analysis' ? 'active' : ''}`}
                            style={{
                                padding: '8px 20px',
                                border: 'none',
                                background: activeTab === 'analysis' ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                                color: activeTab === 'analysis' ? '#4f46e5' : '#1e293b',
                                fontWeight: '600',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                boxShadow: activeTab === 'analysis' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                transition: 'all 0.2s',
                                fontSize: '0.9rem'
                            }}
                        >
                            📊 Analyse
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setActiveTab('data')}
                        className={`co-segmented-control-btn ${activeTab === 'data' ? 'active' : ''}`}
                        style={{
                            padding: '8px 20px',
                            border: 'none',
                            background: activeTab === 'data' ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                            color: activeTab === 'data' ? '#4f46e5' : '#1e293b',
                            fontWeight: '600',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            boxShadow: activeTab === 'data' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                            transition: 'all 0.2s',
                            fontSize: '0.9rem'
                        }}
                    >
                        📁 Stammdaten
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('team')}
                        className={`co-segmented-control-btn ${activeTab === 'team' ? 'active' : ''}`}
                        style={{
                            padding: '8px 20px',
                            border: 'none',
                            background: activeTab === 'team' ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                            color: activeTab === 'team' ? '#4f46e5' : '#1e293b',
                            fontWeight: '600',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            boxShadow: activeTab === 'team' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                            transition: 'all 0.2s',
                            fontSize: '0.9rem'
                        }}
                    >
                        👥 Team & Ressourcen
                    </button>
                </div>

                {/* CONTENT AREA */}
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ 
                        flex: 1, 
                        display: activeTab === 'analysis' ? 'block' : 'none',
                        overflowY: 'auto',
                        padding: '24px'
                    }}>
                        {activeTab === 'analysis' && <ProjectAnalysisTab project={project} currentUser={currentUser} onTabChange={setActiveTab} />}
                    </div>
                    
                    <div style={{ 
                        flex: 1, 
                        display: activeTab === 'data' ? 'block' : 'none',
                        overflowY: 'auto',
                        padding: '24px'
                    }}>
                        <ProjectDataForm
                            ref={dataFormRef}
                            mode={currentMode}
                            initialData={project}
                            onSubmit={(data) => onSubmit(data, currentMode, tempTeamMembers)}
                            onDelete={onDelete}
                            projects={projects}
                            deletedProjects={deletedProjects}
                            onClose={onClose}
                            hideFooter={true}
                        />
                    </div>
                    <div style={{ 
                        flex: 1, 
                        display: activeTab === 'team' ? 'flex' : 'none',
                        flexDirection: 'column',
                        minHeight: 0,
                        padding: '0' /* Team view manages its own layout */
                    }}>
                        <ProjectTeamForm
                            project={project}
                            currentUser={currentUser}
                            tempTeamMembers={tempTeamMembers}
                            onTeamChange={setTempTeamMembers}
                            readOnly={project?.status === 'deleted'}
                            onClose={onClose}
                            onNavigateAway={handleNavigateAway}
                            initialTeamTabs={initialTeamTabs}
                        />
                    </div>
                </div>
            </div>
        </SharedModal>
    );
}
